// Home-delivery tariff helpers. Mirror of PriceCalculator::computeHomeDelivery
// (backend) so the marketing preview + admin preview + address card can all
// render the same numbers without hitting the backend.
//
// The tariff on abroad_offices.delivery_tariff is a single brackets[] table:
//   [{ from_kg, to_kg, transport_per_kg, delivery_tbilisi_gel, delivery_highland_gel }, ...]
// For a package of weight W the row where from_kg < W ≤ to_kg wins (last row
// is clamped for anything above). transport_per_kg is in the office's
// currency ($/€/₾); delivery_* are in ₾. The base price becomes
//   W × transport_per_kg × FX-rate + delivery_gel(zone).
//
// normaliseBrackets() auto-adapts legacy shape ({tbilisi_per_kg,
// highland_per_kg, included_kg, brackets:[{max_kg,...}]}) so the UI keeps
// working before the one-time DB migration script is run.

// Currency-symbol mapping used across the address card, the price table and
// the admin editor. GEL falls back to the ₾ glyph so any office that ever
// runs its tariff in local currency still renders sensibly.
export const currencySymbol = (code) => {
  const c = String(code || "").toUpperCase();
  if (c === "EUR") return "€";
  if (c === "GEL") return "₾";
  return "$";
};

// Cities routed at Highland rates. Anything else falls back to Tbilisi.
// Canonical (Latin) names — must match src/data/georgianCities.js.
export const HIGHLAND_CITIES = new Set([
  "Khulo",
  "Tsageri",
  "Shuakhevi",
  "Keda",
  "Stepantsminda",
  "Oni",
  "Mestia",
  "Lentekhi",
  "Ambrolauri",
]);

// Billed at the Tbilisi courier column; every other non-highland city is a
// region. Mirror of config/delivery_tariffs.php → tbilisi_cities.
export const TBILISI_CITIES = new Set(["Tbilisi"]);

/** "tbilisi" | "region" | "highland" for a canonical city name. */
export const zoneForCity = (city) => {
  if (!city) return "tbilisi";
  if (HIGHLAND_CITIES.has(city)) return "highland";
  return TBILISI_CITIES.has(city) ? "tbilisi" : "region";
};

// Weight rows we render in the preview table. Small on purpose — big enough
// to see the trend, small enough to keep the card compact.
export const PREVIEW_WEIGHTS = [1, 5, 10, 15, 20, 30];

/** true if the given city gets Highland pricing. Case-insensitive. */
export const isHighlandCity = (city) => {
  if (!city) return false;
  const c = String(city);
  for (const h of HIGHLAND_CITIES) {
    if (h.toLowerCase() === c.toLowerCase()) return true;
  }
  return false;
};

/**
 * Normalise the tariff JSON to the new bracket shape regardless of which
 * shape the DB is holding.
 * Returns [{from_kg, to_kg, transport_per_kg, delivery_tbilisi_gel,
 * delivery_highland_gel}, ...] sorted by to_kg asc — empty array if the
 * tariff is missing or malformed.
 */
export const normaliseBrackets = (tariff) => {
  if (!tariff || typeof tariff !== "object") return [];
  const list = Array.isArray(tariff.brackets) ? tariff.brackets : [];
  if (list.length === 0) return [];

  const first = list[0] || {};
  const isNew = first.from_kg != null || first.to_kg != null;

  if (isNew) {
    return list
      .map((b) => {
        const tb = Number(b.transport_per_kg ?? 0);
        return {
          from_kg: Number(b.from_kg ?? 0),
          to_kg: Number(b.to_kg ?? 0),
          transport_per_kg: tb,
          // Optional — fall back to Tbilisi rate when the bracket doesn't
          // define a Highland-specific per-kg.
          transport_per_kg_highland:
            b.transport_per_kg_highland != null
              ? Number(b.transport_per_kg_highland)
              : tb,
          delivery_tbilisi_gel: Number(b.delivery_tbilisi_gel ?? 0),
          // Regions: OnWay bills a third column. Older tariffs without it read
          // as Tbilisi, which is what they were charged before.
          delivery_region_gel: Number(b.delivery_region_gel ?? b.delivery_tbilisi_gel ?? 0),
          delivery_highland_gel: Number(b.delivery_highland_gel ?? 0),
        };
      })
      .sort((a, b) => a.to_kg - b.to_kg);
  }

  // Legacy shape adapter — mirror of PriceCalculator::normaliseHomeDeliveryBrackets.
  const included = Number(tariff.included_kg ?? 10);
  const perKgTb = Number(tariff.tbilisi_per_kg ?? 0);
  const perKgHl =
    tariff.highland_per_kg != null
      ? Number(tariff.highland_per_kg)
      : perKgTb;
  const legacy = [...list].sort(
    (a, b) => Number(a.max_kg ?? 0) - Number(b.max_kg ?? 0)
  );
  const out = [
    {
      from_kg: 0,
      to_kg: included,
      transport_per_kg: perKgTb,
      transport_per_kg_highland: perKgHl,
      delivery_tbilisi_gel: 0,
      delivery_region_gel: 0,
      delivery_highland_gel: 0,
    },
  ];
  let prev = 0;
  for (const br of legacy) {
    const over = Number(br.max_kg ?? 0);
    if (over <= 0) continue;
    out.push({
      from_kg: included + prev,
      to_kg: included + over,
      transport_per_kg: perKgTb,
      // The highland rate holds at every weight, as the server prices it. This
      // copy still dropped to the Tbilisi rate above included_kg, so the USA
      // and Greece tables showed highland parcels cheaper than they are — and
      // the admin editor, which reads through here, would have saved that.
      transport_per_kg_highland: perKgHl,
      delivery_tbilisi_gel: Number(br.delivery_tbilisi_gel ?? 0),
      delivery_region_gel: Number(br.delivery_region_gel ?? br.delivery_tbilisi_gel ?? 0),
      delivery_highland_gel: Number(br.delivery_highland_gel ?? 0),
    });
    prev = over;
  }
  return out;
};

/**
 * The per-kg rates to advertise for a home-delivery office, read from the
 * same brackets the server prices with — never from price / price_scheduled,
 * which do not apply to these offices (Land-China showed 5.90 from the
 * scheduled field while every parcel was charged the tariff's 6.30).
 *
 *   upTo:  the first band (courier included), { normal, highland }
 *   above: the heavier bands, each { min, max } (equal when they agree)
 */
export const tariffHeadline = (office) => {
  const brs = office?.home_delivery_only ? normaliseBrackets(office.delivery_tariff) : [];
  if (brs.length === 0) return null;
  const rate = (b, highland) =>
    Number(highland ? b.transport_per_kg_highland ?? b.transport_per_kg : b.transport_per_kg);
  const span = (vals) => ({ min: Math.min(...vals), max: Math.max(...vals) });
  const rest = brs.slice(1);
  return {
    includedTo: Number(brs[0].to_kg),
    upTo: { normal: rate(brs[0], false), highland: rate(brs[0], true) },
    above: rest.length
      ? { normal: span(rest.map((b) => rate(b, false))), highland: span(rest.map((b) => rate(b, true))) }
      : null,
  };
};

/** "6.30" or "5.90–6.30" for a { min, max } span. */
export const formatRateSpan = (s) =>
  s.min === s.max ? s.min.toFixed(2) : `${s.min.toFixed(2)}–${s.max.toFixed(2)}`;

/**
 * Pick the bracket for the given weight — first row with weight ≤ to_kg
 * wins; anything above clamps to the last row.
 */
export const bracketForWeight = (weight, brackets) => {
  if (!Array.isArray(brackets) || brackets.length === 0) return null;
  const w = Number(weight);
  for (const b of brackets) {
    if (w <= Number(b.to_kg)) return b;
  }
  return brackets[brackets.length - 1];
};

/**
 * Compute the delivery-only total price for a weight to a given zone.
 * Mirror of PriceCalculator::computeHomeDelivery (minus insurance / discount
 * / promo / fine / inner — those belong to per-pack math, not a preview).
 *
 * @param {number} weight            — total weight in kg
 * @param {object} tariff            — abroad_office.delivery_tariff
 * @param {boolean} isHighland       — true when the destination is Highland
 * @param {number|null} fxRate       — office-currency → GEL rate (default 1)
 * @returns {{ transport_fx:number, transport_gel:number, delivery_gel:number,
 *             total_gel:number, bracket:{from_kg,to_kg}|null }}
 */
export const computeDeliveryPrice = (
  weight,
  tariff,
  zoneOrHighland,
  fxRate = null
) => {
  // "tbilisi" | "region" | "highland"; a boolean still means highland or not.
  const zone =
    typeof zoneOrHighland === "boolean"
      ? zoneOrHighland
        ? "highland"
        : "tbilisi"
      : zoneOrHighland || "tbilisi";
  const isHighland = zone === "highland";
  const brackets = normaliseBrackets(tariff);
  const rate = Number(fxRate) > 0 ? Number(fxRate) : 1;
  if (brackets.length === 0) {
    return {
      transport_fx: 0,
      transport_gel: 0,
      delivery_gel: 0,
      total_gel: 0,
      bracket: null,
    };
  }
  const bracket = bracketForWeight(weight, brackets);
  const perKgFx = Number(
    isHighland
      ? bracket?.transport_per_kg_highland ?? bracket?.transport_per_kg ?? 0
      : bracket?.transport_per_kg ?? 0
  );
  const deliveryGel = Number(
    zone === "highland"
      ? bracket?.delivery_highland_gel ?? 0
      : zone === "region"
      ? bracket?.delivery_region_gel ?? bracket?.delivery_tbilisi_gel ?? 0
      : bracket?.delivery_tbilisi_gel ?? 0
  );
  const transportFx = Math.round(Number(weight) * perKgFx * 100) / 100;
  const transportGel = Math.round(transportFx * rate * 100) / 100;
  const totalGel = Math.round((transportGel + deliveryGel) * 100) / 100;
  return {
    transport_fx: transportFx,
    transport_gel: transportGel,
    delivery_gel: deliveryGel,
    total_gel: totalGel,
    bracket: bracket
      ? { from_kg: Number(bracket.from_kg), to_kg: Number(bracket.to_kg) }
      : null,
  };
};
